import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Settings, ClipboardList, Trophy, Target } from 'lucide-react';
import { useTeamStore } from '../../store/teamStore';
import Logo from './Logo';
import WhistleIcon from '../icons/WhistleIcon';
import { hasGames, getEventLabel } from '../../data/sportRulesets';

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const { currentTeam } = useTeamStore();
  const year = new Date().getFullYear();

  // Roster and Lineup manage a multi-player roster, which a personal
  // (parent/player) account's one-player team has no use for. Practices
  // stays available — generating a practice from one athlete's own stats is
  // a core selling point, not a coaching-only feature.
  const isPersonalAccount = Boolean(currentTeam?.isPersonal);
  // GamePlanManagement.tsx hard-blocks every sport except Football with an
  // "only available for football teams" message — only show the link where
  // it actually leads somewhere.
  const showGamePlans = isPersonalAccount && currentTeam?.sport === 'Football';

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    ...(isPersonalAccount ? [] : [{ name: 'Roster', href: '/players', icon: Users }]),
    ...(isPersonalAccount ? [] : [{ name: 'Lineup', href: '/depth-chart', icon: ClipboardList }]),
    { name: 'Practices', href: '/practices', icon: WhistleIcon },
    // { name: 'Playbook', href: '/playbook', icon: BookOpen },
    // Game Plans is otherwise only reachable via the TeamHub tile, which a
    // personal account never sees (no team-switcher to click into TeamHub from).
    ...(showGamePlans ? [{ name: 'Game Plans', href: '/game-plans', icon: Target }] : []),
    // Show Games/Matches for all sports that have event tracking
    ...(hasGames(currentTeam?.sport)
      ? [{ name: getEventLabel(currentTeam?.sport).plural, href: '/games', icon: Trophy }]
      : []),
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  return (
    <div className="flex flex-col w-64 bg-dark text-white min-h-screen p-4">
      <div className="mb-8">
        <Link to="/" className="flex items-center">
          <Logo className="h-10" />
        </Link>
      </div>

      {currentTeam && (
        <div className="mb-6 px-4 py-3 bg-primary bg-opacity-10 rounded-lg">
          <h3 className="font-medium text-sm text-primary">{currentTeam.isPersonal ? 'Profile' : 'Current Team'}</h3>
          <p className="text-white font-semibold">{currentTeam.name}</p>
        </div>
      )}

      <nav className="flex-1">
        <ul className="space-y-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary bg-opacity-20 text-white'
                      : 'text-stone hover:bg-primary hover:bg-opacity-10'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-auto pt-4 border-t border-stone border-opacity-20">
        <div className="px-4 py-2">
          <p className="text-sm text-stone">© {year} HUSTLi LLC. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar; 